import React, { useState, useEffect, useRef } from 'react';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import { Table, Button, Avatar, Tag, Typography, Input, Select, DatePicker, Space, Row, Col, Card, Tooltip, Badge, message, Pagination, Statistic, Form, Modal, Divider } from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CarOutlined, 
  TagOutlined, 
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  FileTextOutlined,
  ClearOutlined,
  TeamOutlined,
  TruckOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  FlagOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  WarningOutlined,
  SaveOutlined,
  SendOutlined,
  CommentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../../API/API';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { isPartStatusUnset } from '../../utils/orderStatus';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const toPST = (date) => {
    if (!date) return null;
    return dayjs(date).tz('America/Los_Angeles');
};

const getPOStatusConfirmedDate = (record) => {
  const confirmedHistory = Array.isArray(record?.purchaseOrderStatusHistory)
    ? record.purchaseOrderStatusHistory
        .filter((entry) => entry?.status === 'PO-CONFIRMED' && entry?.changedAt)
        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
    : [];

  if (confirmedHistory.length > 0) {
    return confirmedHistory[0].changedAt;
  }

  if (record?.purchaseOrderStatus === 'PO-CONFIRMED' && record?.purchaseOrderStatusUpdatedAt) {
    return record.purchaseOrderStatusUpdatedAt;
  }

  return null;
};

const getUserDisplayName = (user) => {
  if (!user || typeof user !== 'object') return null;
  return (
    user.fullName ||
    user.name ||
    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
    user.userName ||
    null
  );
};

// Whoever last touched any Shipment Tracking field (delivery/part/priority/pictures/
// tracking number/shipping company) — mirrors the "changed by" pattern used for
// Document Status on the Order Process page, just rolled up to one shared timestamp
// instead of per-field, since Tracking Status only shows one "Updated" line.
const getShipmentTrackingUpdatedByName = (record) => {
  const history = Array.isArray(record?.ShipmentTrackingHistory) ? record.ShipmentTrackingHistory : [];
  if (history.length === 0) return null;
  const sortedEntries = [...history].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
  return getUserDisplayName(sortedEntries[0]?.changedBy);
};

// Who most recently confirmed the PO status — used on the Date Info column
// alongside getPOStatusConfirmedDate.
const getPOStatusConfirmedByName = (record) => {
  const confirmedHistory = Array.isArray(record?.purchaseOrderStatusHistory)
    ? record.purchaseOrderStatusHistory
        .filter((entry) => entry?.status === 'PO-CONFIRMED' && entry?.changedAt)
        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
    : [];

  return getUserDisplayName(confirmedHistory[0]?.changedBy);
};

const ShipmentTracking = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const maxTotalRef = useRef(0);
  
  // Agent filter state
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loginAgent, setLoginAgent] = useState({});
  
  // Edit states for both statuses
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  
  const [sortedInfo, setSortedInfo] = useState({
    field: 'updatedAt',
    order: 'descend',
  });
  const [filteredInfo, setFilteredInfo] = useState({
    part: null,
    invoiceStatus: null,
    purchaseOrderStatus: null,
    agent: null,
      relocateStatus: null, 
  });
  const [editingStatus, setEditingStatus] = useState({
  rowId: null,
  field: null,
  value: '',
  loading: false
});

const [serviceAgentsMap, setServiceAgentsMap] = useState({});
const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
  const [editingShipmentDate, setEditingShipmentDate] = useState(null);
const [editingRelocatedDate, setEditingRelocatedDate] = useState(null);

  // Search and filter states
  const [searchText, setSearchText] = useState('');
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [searchFields, setSearchFields] = useSearchFields('shipment-tracking:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
  const [dateRange, setDateRange] = useState(() => {
    const now = dayjs();
    return [now.startOf('month'), now.endOf('month')];
  });
  const [quickDateFilter, setQuickDateFilter] = useState('thisMonth');
const [editingShipmentId, setEditingShipmentId] = useState(null);
const [editingShipmentAll, setEditingShipmentAll] = useState(false);
const [modalVisible, setModalVisible] = useState(false);
const [selectedRecord, setSelectedRecord] = useState(null);
const [form] = Form.useForm();
const [updating, setUpdating] = useState(false);

const [shipmentFormData, setShipmentFormData] = useState({
    shippingCompanyName: '',
    trackingStatus: '',
    deliveryStatus: '',
    partStatus: '',
    priority: '',
    picturesStatus: '',
    picturesApproval: ''
});
const [savingShipment, setSavingShipment] = useState(false);
  // Statistics
  const [statistics, setStatistics] = useState({
    totalPending: 0,
    totalAmount: 0,
    averageAmount: 0,
    invoiceSent: 0,
    poConfirmed: 0,
  });
// Add these with your other state declarations
const [serviceAgentModalVisible, setServiceAgentModalVisible] = useState(false);
const [selectedLeadForServiceAgent, setSelectedLeadForServiceAgent] = useState(null);
const [selectedServiceAgentForLead, setSelectedServiceAgentForLead] = useState(null);
const [savingServiceAgent, setSavingServiceAgent] = useState(false);
  const isAdmin = loginAgent?.role === "ADMIN";
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
      const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
      const [newComment, setNewComment] = useState('');
      const [addingComment, setAddingComment] = useState(false);
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};

      // Handle opening the service agent selection modal
const handleServiceAgentEdit = (record) => {
    const currentServiceAgentId = record.serviceHandledBy?._id || record.serviceHandledBy;
    setSelectedLeadForServiceAgent(record);
    setSelectedServiceAgentForLead(currentServiceAgentId || null);
    setServiceAgentModalVisible(true);
};

// Handle saving the service agent
const handleServiceAgentSave = async () => {
    if (!selectedLeadForServiceAgent) return;
    
    setSavingServiceAgent(true);
    
    try {
        const payload = {
            serviceHandledBy: selectedServiceAgentForLead || null // Set to null if unassigned
        };
        
        const response = await API.UpdateLead(selectedLeadForServiceAgent._id, payload);
        
        if (response?.status || response?.success) {
            message.success(selectedServiceAgentForLead ? 'Service agent assigned successfully!' : 'Service agent unassigned successfully!');
            
            // Update local state
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === selectedLeadForServiceAgent._id ? { 
                        ...lead, 
                        serviceHandledBy: selectedServiceAgentForLead
                    } : lead
                )
            );
            
            // Close modal
            setServiceAgentModalVisible(false);
            setSelectedLeadForServiceAgent(null);
            setSelectedServiceAgentForLead(null);
            
            // Refresh leads to get populated agent data
            fetchLeads(true);
            fetchServiceAgents(); // Refresh the agents map
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to save service agent assignment');
    } finally {
        setSavingServiceAgent(false);
    }
};

      const handleAddComment = async () => {
      if (!newComment.trim()) {
          message.warning('Please enter a comment');
          return;
      }
  
      if (!selectedLeadForComment) return;
  
      setAddingComment(true);
      try {
          const existingComments = selectedLeadForComment.trackingPageComment || [];
          const payload = {
              trackingPageComment: [
                  ...existingComments,
                  {
                      comment: newComment.trim(),
                      changedAt: new Date().toISOString()
                  }
              ]
          };
  
          const response = await API.UpdateLead(selectedLeadForComment._id, payload);
  
          if (response?.status) {
              message.success('Comment added successfully!');
              setNewComment('');
              setCommentsModalVisible(false);
              setSelectedLeadForComment(null);
              // Refresh the leads list
              fetchLeads();
          } else {
              message.error(response?.message || 'Failed to add comment');
          }
      } catch (error) {
          console.error('Error adding comment:', error);
          message.error('Failed to add comment');
      } finally {
          setAddingComment(false);
      }
  };
  const formatCommentDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit"
      });
  };
  
// Initialize form data with current shipment values
const initializeShipmentForm = (record) => {
    const tracking = record.ShipmentTracking || {};
    setShipmentFormData({
        shippingCompanyName: tracking.shippingCompanyName || '',
        trackingStatus: tracking.trackingStatus || '',
        deliveryStatus: tracking.deliveryStatus || 'Not Delivered',
        partStatus: tracking.partStatus || 'Not Available',
        priority: tracking.priority || 'Medium',
        picturesStatus: tracking.picturesStatus || 'Not Received',
        picturesApproval: tracking.picturesApproval || 'No'
    });
};

  // Get user data on mount
  useEffect(() => {
    const UD = localStorage.getItem('userData');
    if (!UD) {
      navigate(ROUTES?.LOGIN);
      return;
    }
    const parsedUser = JSON.parse(UD);
    setLoginAgent(parsedUser);
    
    if (parsedUser?.role === "ADMIN") {
      fetchAgents();
    }
       fetchServiceAgents();
  }, [navigate]);

  const fetchAgents = async () => {
    try {
      const response = await API.userGetAll();
      if (response?.success) {
        const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
        setAgents(salesAgents);
      }
    } catch (error) {
      console.error("Failed to fetch agents", error);
    }
  };
const getServiceAgentName = (record) => {
    const serviceAgentId = record.serviceHandledBy?._id || record.serviceHandledBy;
    if (!serviceAgentId) return null;
    
    // Check if already populated as object
    if (record.serviceHandledBy && typeof record.serviceHandledBy === 'object') {
        return `${record.serviceHandledBy.firstName || ''} ${record.serviceHandledBy.lastName || ''}`.trim() || 
               record.serviceHandledBy.userName || 'Service Agent';
    }
    
    // Check in our map
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            // Get all users with SERVICE-AGENT or SUPPORT-AGENT role
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};
  // Get agent name function
  const getAgentName = (record) => {
    if (record.assignedAgent?.name) return record.assignedAgent.name;
    if (record.agentName) return record.agentName;
    if (record.handledBy?.firstName && record.handledBy?.lastName) {
      return `${record.handledBy.firstName} ${record.handledBy.lastName}`;
    }
    if (record.handledBy?.name) return record.handledBy.name;
    return 'Unassigned';
  };

  // Get unique agents for filter
  const getUniqueAgents = () => {
    const agentNames = leads
      .map(lead => getAgentName(lead))
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return agentNames;
  };

  // Get unique values for filters
  const getUniqueValues = (field) => {
    const values = leads
      .map(lead => {
        if (field === 'part') {
          return lead.vehicleInfo?.part;
        }
        return lead[field];
      })
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return values;
  };

  // Calculate statistics
  const calculateStatistics = (data) => {
    const totalPending = data.length;
    const totalAmount = data.reduce((sum, lead) => sum + (lead.totalAmount || 0), 0);
    const averageAmount = totalPending > 0 ? totalAmount / totalPending : 0;
    const invoiceSent = data.filter(lead => lead.invoiceStatus === 'INVOICE-SENT').length;
    const poConfirmed = data.filter(lead => lead.purchaseOrderStatus === 'PO-CONFIRMED').length;
    
    setStatistics({
      totalPending,
      totalAmount,
      averageAmount,
      invoiceSent,
      poConfirmed,
    });
  };

  // Get date range based on quick filter
  const getQuickDateRange = (filterType) => {
    const now = dayjs();
    let startDate = now;
    let endDate = now;

    switch (filterType) {
      case 'today':
        startDate = now.startOf('day');
        endDate = now.endOf('day');
        break;
      case 'yesterday':
        startDate = now.subtract(1, 'day').startOf('day');
        endDate = now.subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        startDate = now.startOf('week');
        endDate = now.endOf('week');
        break;
      case 'thisMonth':
        startDate = now.startOf('month');
        endDate = now.endOf('month');
        break;
      case 'lastMonth':
        startDate = now.subtract(1, 'month').startOf('month');
        endDate = now.subtract(1, 'month').endOf('month');
        break;
      case 'thisQuarter':
        startDate = now.startOf('quarter');
        endDate = now.endOf('quarter');
        break;
      case 'thisYear':
        startDate = now.startOf('year');
        endDate = now.endOf('year');
        break;
      default:
        return null;
    }

    return [startDate, endDate];
  };

  // Set default date range to current month on component mount
  useEffect(() => {
    const defaultDateRange = getQuickDateRange('thisMonth');
    setDateRange(defaultDateRange);
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    maxTotalRef.current = 0;
  }, [searchQuery, effectiveSearchFields, dateRange, filteredInfo, sortedInfo.field, sortedInfo.order, selectedAgentId]);

  // Handle quick date filter change
  const handleQuickDateFilter = (value) => {
    setQuickDateFilter(value);
    const newDateRange = getQuickDateRange(value);
    setDateRange(newDateRange);
    setCurrentPage(1);
  };

  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const isSearching = Boolean(searchQuery);
      const workflowPurchaseOrderStatuses = ['PO-CONFIRMED'];
      const params = {
        page: currentPage,
        limit: pageSize,
        status: "Sales Made",
        purchaseOrderStatus: workflowPurchaseOrderStatuses.join(','),
        trackingComplete: 'false',
        sortBy: sortedInfo.field || 'updatedAt',
        order: sortedInfo.order === 'ascend' ? 'asc' : 'desc',
      };

      if (isSearching) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      } else {
        params.dateField = 'purchaseOrderStatusConfirmedDate';

        // Add null check for dateRange
        if (dateRange && dateRange[0] && dateRange[1]) {
          params.startDate = dateRange[0].startOf('day').toISOString();
          params.endDate = dateRange[1].endOf('day').toISOString();
        }
      }

      if (filteredInfo.part) {
        params.part = filteredInfo.part.join(',');
      }

      if (filteredInfo.invoiceStatus) {
        params.invoiceStatus = filteredInfo.invoiceStatus.join(',');
      }

 if (filteredInfo.purchaseOrderStatus) {
        const narrowedStatuses = filteredInfo.purchaseOrderStatus.filter((status) =>
          workflowPurchaseOrderStatuses.includes(status)
        );
        params.purchaseOrderStatus = narrowedStatuses.length
          ? narrowedStatuses.join(',')
          : '__NO_MATCH__';
      }

      if (isAdmin && selectedAgentId) {
        params.agentId = selectedAgentId;
      }

      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['shipment-tracking']).
      params.view = 'shipment-tracking';
      const response = await API.leadGetAll(params);
      
      if (response.status && response.data) {
        setLeads(response.data);
        calculateStatistics(response.data);
        
        const possibleTotal = response.pagination?.totalRecords || 
                              response.total || 
                              response.totalRecords ||
                              response.data.length;
        
        setTotalRecords(possibleTotal);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      message.error('Failed to fetch leads');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchLeads(true);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Handle date range change
   const handleDateRangeChange = (dates) => {
    if (!dates || dates.length === 0) {
      // When cleared, set to default date range (this month)
      const defaultRange = getQuickDateRange('thisMonth');
      setDateRange(defaultRange);
      setQuickDateFilter('thisMonth');
    } else {
      setDateRange(dates);
      setQuickDateFilter('custom');
    }
  };
  // Handle clear filters
  const handleClearFilters = () => {
    setSearchText('');
    const defaultDateRange = getQuickDateRange('thisMonth');
    setDateRange(defaultDateRange);
    setQuickDateFilter('thisMonth');
    setFilteredInfo({ part: null, invoiceStatus: null, purchaseOrderStatus: null, agent: null });
    setSelectedAgentId(null);
    setSortedInfo({ field: 'updatedAt', order: 'descend' });
    setCurrentPage(1);
    maxTotalRef.current = 0;
    message.info({
      content: "All filters cleared",
      icon: <ClearOutlined />
    });
  };

  // Handle view button click
  const handleView = (record) => {
    setSelectedLeadId(record._id);
    navigate(`/lead-details/${record._id}`, {
      state: { from: '/shipment-tracking' }
    });
  };

  // Handle table change
  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

const handleEditStart = (record, field) => {
  setEditingId(record._id);
  setEditingField(field);
  setEditingValue(record[field] || 
    (field === 'invoiceStatus' ? 'INVOICE-NOT-SENT' : 
     field === 'purchaseOrderStatus' ? 'PO-NOT-SENT' : ''));
  
  // If editing relocateStatus, also set the date
  if (field === 'relocateStatus') {
    const existingDate = record.relocateStatusUpdatedAt || new Date();
    setEditingRelocatedDate(dayjs(existingDate));
  }
};
  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
  };

const handleEditSave = async (id) => {
  try {
    const updateData = {};
    updateData[editingField] = editingValue;
    
    // If editing relocateStatus, also update the timestamp with selected date
    if (editingField === 'relocateStatus') {
      if (!editingRelocatedDate) {
        message.warning('Please select a date');
        return;
      }
      updateData.relocateStatusUpdatedAt = editingRelocatedDate.toISOString();
    }
    
    const response = await API.UpdateLead(id, updateData);
    
    if (response.status) {
      message.success(`${editingField === 'invoiceStatus' ? 'Invoice' : editingField === 'purchaseOrderStatus' ? 'PO' : editingField === 'relocateStatus' ? 'Relocate Status' : editingField} updated successfully`);
      setEditingId(null);
      setEditingField(null);
      setEditingValue('');
      setEditingRelocatedDate(null);
      await fetchLeads();
    } else {
      message.error('Failed to update status');
    }
  } catch (error) {
    console.error('Error updating status:', error);
    message.error('Error updating status');
  }
};
const handleShipmentEditStart = (record, field, currentValue) => {
    setEditingShipmentId(record._id);
    
    if (field === null) {
        // Edit All mode
        setEditingShipmentAll(true);
        initializeShipmentForm(record);
        // Get the latest ShipmentTrackingUpdatedAt date
        const existingDate = record.ShipmentTrackingUpdatedAt || new Date();
        setEditingShipmentDate(dayjs(existingDate));
    } else {
        // Single field edit mode
        setEditingShipmentAll(false);
        setEditingShipmentField(field);
        setEditingShipmentValue(currentValue || '');
        // Get the latest change date for this specific field from history
        const historyField = `${field}History`;
        const history = record[historyField] || [];
        const latestEntry = [...history].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0];
        const existingDate = latestEntry?.changedAt || record.ShipmentTrackingUpdatedAt || new Date();
        setEditingShipmentDate(dayjs(existingDate));
    }
};

// Handle Shipment edit cancel
const handleShipmentEditCancel = () => {
    setEditingShipmentId(null);
    setEditingShipmentAll(false);
    setEditingShipmentField(null);
    setEditingShipmentValue(null);
    setShipmentFormData({
        shippingCompanyName: '',
        trackingStatus: '',
        deliveryStatus: '',
        partStatus: '',
        priority: '',
        picturesStatus: '',
        picturesApproval: ''
    });
};

// Handle Shipment edit save - Saves ALL fields at once with date
const handleShipmentEditSave = async (id) => {
    if (!editingShipmentDate) {
        message.warning('Please select a date');
        return;
    }
    
    setSavingShipment(true);

    try {
        // Get current lead to preserve other shipment fields
        const currentLead = leads.find(lead => lead._id === id);
        const currentShipment = currentLead?.ShipmentTracking || {};
        
        let updatedShipment;
        let historyUpdates = [];
        const changeDate = editingShipmentDate.toISOString();
        
        if (editingShipmentAll) {
            // Save ALL fields from the form
            updatedShipment = {
                ...currentShipment,
                ...shipmentFormData,
                ShipmentTrackingUpdatedAt: changeDate
            };
            
            // Create history entries for each changed field with the selected date
            Object.keys(shipmentFormData).forEach(field => {
                const oldValue = currentShipment[field];
                const newValue = shipmentFormData[field];
                
                if (oldValue !== newValue) {
                    historyUpdates.push({
                        field: `ShipmentTracking.${field}`,
                        oldValue: oldValue,
                        newValue: newValue,
                        changedAt: changeDate,
                        changedBy: loginAgent?.firstName + ' ' + loginAgent?.lastName || 'Admin'
                    });
                }
            });
        } else {
            // Save single field
            updatedShipment = {
                ...currentShipment,
                [editingShipmentField]: editingShipmentValue,
                ShipmentTrackingUpdatedAt: changeDate
            };
            
            const historyField = `${editingShipmentField}History`;
            const existingHistory = currentLead[historyField] || [];
            
            historyUpdates = [
                ...existingHistory,
                {
                    field: `ShipmentTracking.${editingShipmentField}`,
                    oldValue: currentShipment[editingShipmentField],
                    newValue: editingShipmentValue,
                    changedAt: changeDate,
                    changedBy: loginAgent?.firstName + ' ' + loginAgent?.lastName || 'Admin'
                }
            ];
        }

        const payload = {
            ShipmentTracking: updatedShipment,
            ShipmentTrackingUpdatedAt: changeDate
        };
        
        // Add history fields to payload
        if (editingShipmentAll) {
            historyUpdates.forEach(update => {
                const historyField = update.field.split('.')[1] + 'History';
                if (!payload[historyField]) payload[historyField] = [];
                payload[historyField].push(update);
            });
        } else {
            const historyField = `${editingShipmentField}History`;
            payload[historyField] = historyUpdates;
        }

        const response = await API.UpdateLead(id, payload);

        if (response?.status) {
            const fieldLabels = {
                shippingCompanyName: 'Shipping Company',
                trackingStatus: 'Tracking Number',
                deliveryStatus: 'Delivery Status',
                partStatus: 'Part Status',
                priority: 'Priority',
                picturesStatus: 'Pictures Status',
                picturesApproval: 'Pictures Approval'
            };
            
            if (editingShipmentAll) {
                message.success('All shipment details updated successfully!');
            } else {
                message.success(`${fieldLabels[editingShipmentField] || editingShipmentField} updated successfully!`);
            }
            
            // Update local state
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === id ? { 
                        ...lead, 
                        ShipmentTracking: updatedShipment,
                        ShipmentTrackingUpdatedAt: changeDate
                    } : lead
                )
            );
            
            // Reset editing states
            setEditingShipmentId(null);
            setEditingShipmentAll(false);
            setEditingShipmentField(null);
            setEditingShipmentValue(null);
            setEditingShipmentDate(null);
            setShipmentFormData({
                shippingCompanyName: '',
                trackingStatus: '',
                deliveryStatus: '',
                partStatus: '',
                priority: '',
                picturesStatus: '',
                picturesApproval: ''
            });
            
            // Refresh leads to get updated data
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to update shipment status');
        }
    } catch (error) {
        console.error('Error updating shipment:', error);
        message.error('Failed to save shipment status');
    } finally {
        setSavingShipment(false);
    }
};
  // Load leads on mount and when dependencies change
  useEffect(() => {
    fetchLeads();
  }, [
    currentPage, 
    pageSize, 
    sortedInfo.field, 
    sortedInfo.order, 
    filteredInfo.part,
    filteredInfo.invoiceStatus,
    filteredInfo.purchaseOrderStatus,
    searchQuery,
    effectiveSearchFields,
    dateRange,
    selectedAgentId,
    selectedServiceAgentId
  ]);

  const isSearching = searchText?.trim().length > 0;
const getLatestStatusWithDate = (record) => {
  const statusHistory = record.zohoStatusHistory || [];
  if (statusHistory.length === 0) {
    return { status: record.zohoStatus || 'NOT-SENT', date: null };
  }
  const latest = [...statusHistory].sort((a, b) => 
    new Date(b.changedAt) - new Date(a.changedAt)
  )[0];
  return { status: latest.status, date: latest.changedAt };
};
  // Agent column definition
  const agentColumn = isAdmin ? [{
    title: 'Sales-Agent',
    key: 'agent',
    width: 160,
    sorter: (a, b) => getAgentName(a).localeCompare(getAgentName(b)),
   
    render: (_, record) => {
      const agentName = getAgentName(record);
      const isUnassigned = agentName === 'Unassigned';
      return (
        <Tooltip title={agentName}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              size={32}
              icon={<UserOutlined />}
              style={{
                backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                flexShrink: 0,
              }}
            >
              {!isUnassigned && agentName?.[0]?.toUpperCase()}
            </Avatar>
            <Text
              ellipsis={{ tooltip: true }}
              style={{
                fontSize: 13,
                color: isUnassigned ? '#8c8c8c' : '#1f2937',
                fontStyle: isUnassigned ? 'italic' : 'normal',
              }}
            >
              {agentName}
            </Text>
          </div>
        </Tooltip>
      );
    },
  }] : [];

  const columns = [
{
  title: "Order Number",
  width: 120, // Slightly increased width to accommodate date
  key: "orderNumber",
  render: (_, record) => {
    const hasOrderNumber = record.orderNumber;
    
    return (
      <div>
        {/* Order Number Section */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: hasOrderNumber ? '#f0f0ff' : '#fff1f0',
          padding: '4px 8px',
          borderRadius: 8,
          width: 'fit-content'
        }}>
          {hasOrderNumber ? (
            <FileTextOutlined style={{ color: '#722ed1', fontSize: 12 }} />
          ) : (
            <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
          )}
          <Text style={{ 
            color: hasOrderNumber ? '#722ed1' : '#ff4d4f',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'monospace',
            fontStyle: hasOrderNumber ? 'normal' : 'italic'
          }}>
            {hasOrderNumber ? `#${record.orderNumber.slice(-8)}` : 'Waiting..'}
          </Text>
          {hasOrderNumber && (
            <Tooltip title="Copy Order Number">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined style={{ fontSize: 10 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(record.orderNumber);
                  message.success('Order number copied!');
                }}
                style={{
                  padding: 0,
                  height: 18,
                  width: 18,
                  minWidth: 18,
                  color: '#722ed1',
                }}
              />
            </Tooltip>
          )}
        </div>
        
        {/* Date Section - Shows Order Date or Created Date */}
        {(record.orderDate || record.createdAt) && (
          <div style={{ 
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            paddingLeft: 4
          }}>
            <CalendarOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
            <Tooltip 
              title={dayjs(record.orderDate || record.createdAt).format('MMM DD, YYYY hh:mm A')}
            >
              <Text type="secondary" style={{ fontSize: 10 }}>
                {record.orderDate 
                  ? dayjs(record.orderDate).format('MM/DD/YY hh:mm A')
                  : `Created: ${dayjs(record.createdAt).format('MM/DD/YY')}`
                }
              </Text>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
},
    ...agentColumn,
   {
    title: 'Shipment Service-Agent',
    key: 'serviceAgent',
    width: 180,
    sorter: (a, b) => {
        const nameA = getServiceAgentName(a) || '';
        const nameB = getServiceAgentName(b) || '';
        return nameA.localeCompare(nameB);
    },
    render: (_, record) => {
        const serviceAgentName = getServiceAgentName(record);
        const isAssigned = !!serviceAgentName;
        
        return (
            <div 
                style={{ cursor: 'pointer' }}
                onClick={() => handleServiceAgentEdit(record)}
            >
                <Tooltip title={isAssigned ? `Click to change service agent` : 'Click to assign service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text
                                        ellipsis={{ tooltip: true }}
                                        style={{
                                            fontSize: 13,
                                            color: '#1f2937',
                                            display: 'block',
                                            fontWeight: 500
                                        }}
                                    >
                                        {serviceAgentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        Click to edit
                                    </Text>
                                </>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#1890ff',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    + Assign Agent
                                </Text>
                            )}
                        </div>
                        <EditOutlined 
                            style={{ 
                                fontSize: 12, 
                                color: '#1890ff',
                                opacity: 0.6
                            }} 
                        />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
    {
      title: 'Customer',
      key: 'customer',
      fixed: 'left',
      width: 320,
      sorter: true,
      sortOrder: sortedInfo.field === 'customer' && sortedInfo.order,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar 
            size={44}
            icon={<UserOutlined />} 
            style={{ 
              background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
              boxShadow: '0 4px 12px rgba(24,144,255,0.2)',
              flexShrink: 0
            }}
          />
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
              {record.personalInfo?.fullName || 'N/A'}
            </Text>
            <Space direction="vertical" size={2}>
              <Space size={4}>
                <MailOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.personalInfo?.email || 'N/A'}
                </Text>
              </Space>
              <Space size={4}>
                <PhoneOutlined style={{ color: '#52c41a', fontSize: 11 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.personalInfo?.phoneNumber || 'N/A'}
                </Text>
              </Space>
            </Space>
          </div>
        </div>
      ),
    },
    {
      title: 'Vehicle & Part',
      key: 'vehicle',
      width: 180,
      render: (_, record) => (
        <Card size="small" style={{ borderRadius: 10, background: '#fafafa', border: 'none' }} bodyStyle={{ padding: '8px 12px' }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space size={4}>
              <CarOutlined style={{ color: '#faad14', fontSize: 12 }} />
              <Text strong style={{ fontSize: 13 }}>
                {record.vehicleInfo?.year || ''} {record.vehicleInfo?.make || ''}
              </Text>
            </Space>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 18 }}>
              {record.vehicleInfo?.model || 'N/A'}
            </Text>
            {record.vehicleInfo?.variant && (
              <Tag style={{ marginLeft: 18, marginTop: 2, fontSize: 10 }}>
                {record.vehicleInfo.variant}
              </Tag>
            )}
            <div style={{ marginTop: 6, borderTop: '1px dashed #e8e8e8', paddingTop: 6 }}>
              <Space size={4}>
                <TagOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Tag color="cyan" style={{ fontSize: 11 }}>
                  {record.vehicleInfo?.part || 'N/A'}
                </Tag>
              </Space>
            </div>
          </Space>
        </Card>
      ),
    },
         {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
{
  title: 'Tracking Status',
  key: 'shipmentTracking',
  width: 320,
  render: (_, record) => {
    const tracking = record.ShipmentTracking || {};
    const lastUpdated = record.ShipmentTrackingUpdatedAt;
    const lastUpdatedPST = lastUpdated ? toPST(lastUpdated) : null;
    const lastUpdatedByName = getShipmentTrackingUpdatedByName(record);

    const getPartStatusColor = (status) => {
      switch(status) {
        case 'Available': return '#52c41a';
        case 'Not Available': return '#f5222d';
        case 'Not Good': return '#faad14';
        case 'Wrong Part': return '#f5222d';
        case 'Customer Canceled Order': return '#8c8c8c';
        default: return '#d9d9d9';
      }
    };
    
    const getPartStatusIcon = (status) => {
      switch(status) {
        case 'Available': return <CheckCircleOutlined />;
        case 'Not Available': return <CloseCircleOutlined />;
        case 'Not Good': return <ExclamationCircleOutlined />;
        case 'Wrong Part': return <CloseCircleOutlined />;
        case 'Customer Canceled Order': return <StopOutlined />;
        default: return null;
      }
    };
    
    const getPriorityConfig = (priority) => {
      switch(priority) {
        case 'High': return { color: '#f5222d', icon: <ExclamationCircleOutlined /> };
        case 'Medium': return { color: '#fa8c16', icon: <FlagOutlined /> };
        case 'Low': return { color: '#52c41a', icon: <CheckCircleOutlined /> };
        default: return { color: '#8c8c8c', icon: null };
      }
    };
    
    const priorityConfig = getPriorityConfig(tracking.priority);

    // Shared placeholder for any tracking field that hasn't been set yet, so
    // "empty" always reads the same way instead of each field faking a default
    // status (e.g. "Not Received"/"Not Available"/"Medium") that was never chosen.
    const renderStatusValueTag = (value, { color, icon } = {}) => {
      if (!value) {
        return (
          <Tooltip title="Not set — click the edit icon to update">
            <Tag
              style={{
                fontSize: 11,
                marginTop: 4,
                color: '#bfbfbf',
                background: '#fafafa',
                borderStyle: 'dashed',
                borderColor: '#d9d9d9'
              }}
            >
              Not Set
            </Tag>
          </Tooltip>
        );
      }

      return (
        <Tag color={color} icon={icon} style={{ fontSize: 11, marginTop: 4 }}>
          {value}
        </Tag>
      );
    };

const showEditModal = () => {
  setSelectedRecord(record);
  form.setFieldsValue({
    trackingStatus: tracking?.trackingStatus,
    shippingCompanyName: tracking?.shippingCompanyName,
    // Leave empty (required) until someone has chosen it, so the stored default
    // "Not Available" can't be saved by accident and reset the PO
    partStatus: isPartStatusUnset(record) ? undefined : tracking?.partStatus,
    picturesApproval: tracking?.picturesApproval,
    picturesStatus: tracking?.picturesStatus,
    priority: tracking?.priority,
  });
  // Set the date to the last updated date
  const lastUpdated = record.ShipmentTrackingUpdatedAt || new Date();
  setEditingShipmentDate(dayjs(lastUpdated));
  setModalVisible(true);
};
    
    // NORMAL VIEW MODE - Display shipment details
    return (
      <div style={{ 
        background: '#f8f9fa', 
        borderRadius: 12, 
        padding: '12px',
        border: '1px solid #f0f0f0',
        position: 'relative'
      }}>
        {/* Edit Button - Top Right */}
        {/* {isAdmin && ( */}
          <Tooltip title="Edit Shipment Details">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ fontSize: 12 }} />}
              onClick={showEditModal}
              style={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#1890ff'
              }}
            />
          </Tooltip>
        {/* )} */}
        
        {/* Shipping Company & Tracking Number */}
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TruckOutlined style={{ color: '#1890ff', fontSize: 14 }} />
              <Text strong style={{ fontSize: 13 }}>
                {tracking.shippingCompanyName || 'Not Assigned'}
              </Text>
            </div>
            {tracking.trackingStatus && (
              <div style={{ 
                background: '#e6f7ff', 
                padding: '4px 8px', 
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Text code style={{ fontSize: 11, flex: 1 }}>
                  {tracking.trackingStatus}
                </Text>
                <Tooltip title="Copy Tracking Number">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ fontSize: 10 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(tracking.trackingStatus);
                      message.success('Tracking number copied!');
                    }}
                    style={{ padding: 0, height: 18, width: 18 }}
                  />
                </Tooltip>
              </div>
            )}
          </Space>
        </div>
        
        {/* Delivery & Part Status */}
        <div style={{ marginBottom: 12 }}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Delivery</Text>
                <div>
                  {renderStatusValueTag(tracking.deliveryStatus, {
                    color: tracking.deliveryStatus === 'Delivered' ? 'success' : 'error'
                  })}
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Part</Text>
                <div>
                  {renderStatusValueTag(isPartStatusUnset(record) ? undefined : tracking.partStatus, {
                    color: getPartStatusColor(tracking.partStatus),
                    icon: getPartStatusIcon(tracking.partStatus)
                  })}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        
        {/* Priority & Pictures Status */}
        <div style={{ marginBottom: 12 }}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Priority</Text>
                <div style={{ marginTop: 4 }}>
                  {renderStatusValueTag(tracking.priority, {
                    color: priorityConfig.color,
                    icon: priorityConfig.icon
                  })}
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Pictures</Text>
                <div>
                  {renderStatusValueTag(tracking.picturesStatus, {
                    color: tracking.picturesStatus === 'Received' ? 'success' : 'default'
                  })}
                </div>
              </div>
            </Col>
          </Row>
            {/* Pictures Approval Badge */}
        {tracking.picturesApproval === 'Yes' && (
          <div style={{ marginTop: 8 }}>
            <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 10 }}>
              Pictures Approved
            </Tag>
          </div>
        )}
        </div>
        
    
        
      

            {/* Last Updated */}
        {lastUpdatedPST && (
          <div style={{
            borderTop: '1px dashed #e8e8e8',
            paddingTop: 8,
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Tooltip title={lastUpdatedPST.format('MMM-DD-YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <SyncOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Updated: {lastUpdatedPST.format('MM/DD/YY · hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
            {lastUpdatedByName && (
              <Space size={4}>
                <UserOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Updated by: {lastUpdatedByName}
                </Text>
              </Space>
            )}
          </div>
        )}
      </div>
    );
  },
},
{
  title: 'Relocated',
  key: 'relocated',
  width: 200, // Increased width to accommodate date picker
  align: 'center',
  render: (_, record) => {
    const relocateStatus = record.relocateStatus || 'No';
    const relocateStatusUpdatedAt = record.relocateStatusUpdatedAt;
    const relocateUpdatedPST = relocateStatusUpdatedAt ? toPST(relocateStatusUpdatedAt) : null;
    const isEditing = editingId === record._id && editingField === 'relocateStatus';
    
    if (isEditing) {
      return (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Select
            value={editingValue}
            onChange={(value) => setEditingValue(value)}
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="Yes">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Yes</span>
              </Space>
            </Option>
            <Option value="No">
              <Space>
                <CloseCircleOutlined style={{ color: '#f5222d' }} />
                <span>No</span>
              </Space>
            </Option>
          </Select>
          <DatePicker
            value={editingRelocatedDate}
            onChange={setEditingRelocatedDate}
            format="MM/DD/YYYY"
            size="small"
            placeholder="Select date"
            style={{ width: '100%' }}
          />
          <Space size={4}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />} 
              onClick={() => handleEditSave(record._id)}
              style={{ flex: 1, borderRadius: 6 }}
            />
            <Button 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={() => {
                setEditingId(null);
                setEditingField(null);
                setEditingValue('');
                setEditingRelocatedDate(null);
              }}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </Space>
      );
    }
    
    return (
      <div>
        <Space size={4}>
          <Tag 
            color={relocateStatus === 'Yes' ? 'success' : 'error'}
            icon={relocateStatus === 'Yes' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ 
              padding: '4px 12px', 
              borderRadius: 16, 
              fontWeight: 500, 
              cursor: 'pointer',
              margin: 0
            }}
            onClick={() => handleEditStart(record, 'relocateStatus')}
          >
            {relocateStatus === 'Yes' ? '✓ Yes' : '✗ No'}
          </Tag>
          <Tooltip title="Edit Relocate Status with Date">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditStart(record, 'relocateStatus')}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
        {relocateUpdatedPST && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={relocateUpdatedPST.format('MMM-DD-YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Since: {relocateUpdatedPST.format('MM/DD/YY · hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
{
  title: 'Zoho',
  key: 'zohoStatus',
  width: 160,
  align: 'center',
  filters: [
    { text: 'NOT-SENT', value: 'NOT-SENT' },
    { text: 'SENT', value: 'SENT' },
    
    { text: 'INCOMPLETE', value: 'INCOMPLETE' },
    { text: 'FAILED', value: 'FAILED' },
  ],
  filteredValue: filteredInfo.zohoStatus || null,
  render: (_, record) => {
    const isEditing = editingStatus.rowId === record._id && editingStatus.field === 'zohoStatus';
    const latestStatusInfo = getLatestStatusWithDate(record);
    const status = latestStatusInfo.status;
    const statusDate = latestStatusInfo.date;
    const statusDatePST = statusDate ? toPST(statusDate) : null;

    const getStatusColor = (status) => {
      const colors = {
        'NOT-SENT': 'default',
    
        'COMPLETED': 'success',
        'INCOMPLETE': 'warning',
        'FAILED': 'error',
      };
      return colors[status] || 'default';
    };

    const getStatusIcon = (status) => {
      switch(status) {
        case 'SENT': return <SendOutlined />;
       
        case 'COMPLETED': return <CheckCircleOutlined />;
        case 'INCOMPLETE': return <ExclamationCircleOutlined />;
        case 'FAILED': return <CloseCircleOutlined />;
        default: return null;
      }
    };

    if (isEditing) {
      return (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Select
            value={editingStatus.value}
            onChange={(val) => setEditingStatus(prev => ({ ...prev, value: val }))}
            style={{ width: '100%' }}
            size="small"
            disabled={editingStatus.loading}
          >
            {['NOT-SENT', 'SENT', 'COMPLETED', 'INCOMPLETE', 'FAILED'].map(opt => (
              <Option key={opt} value={opt}>{opt.replace(/-/g, ' ')}</Option>
            ))}
          </Select>
          <Space size={4}>
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              onClick={async () => {
                setEditingStatus(prev => ({ ...prev, loading: true }));
                const success = await updateFieldWithHistory(
                  record, 
                  'zohoStatus', 
                  editingStatus.value,
                  'zohoStatusHistory',
                  'zohoStatusUpdatedAt'
                );
                if (success) {
                  setEditingStatus({ rowId: null, field: null, value: '', loading: false });
                  message.success('Zoho status updated successfully');
                } else {
                  setEditingStatus(prev => ({ ...prev, loading: false }));
                  message.error('Failed to update status');
                }
              }}
              loading={editingStatus.loading}
              style={{ flex: 1 }}
            >
              Save
            </Button>
            <Button 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={() => setEditingStatus({ rowId: null, field: null, value: '', loading: false })}
            />
          </Space>
        </Space>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' }}>
          <Tag 
            color={getStatusColor(status)} 
            icon={getStatusIcon(status)}
            style={{ 
              padding: '4px 10px', 
              borderRadius: 16, 
              fontWeight: 500,
              margin: 0,
              flex: 1,
              textAlign: 'center'
            }}
          >
            {status.replace(/-/g, ' ')}
          </Tag>
          {isAdmin && (
            <Tooltip title="Edit Zoho Status">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ fontSize: 12 }} />}
                onClick={() => setEditingStatus({
                  rowId: record._id,
                  field: 'zohoStatus',
                  value: status,
                  loading: false,
                })}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
        </div>
        {statusDatePST && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={statusDatePST.format('MM/DD/YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {statusDatePST.format('MM/DD/YY · hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
{
  title: 'Invoice',
  key: 'invoiceStatus',
  width: 180,
  align: 'center',
  filters: [
    { text: 'Invoice Sent', value: 'INVOICE-SENT' },
    { text: 'Invoice Not Sent', value: 'INVOICE-NOT-SENT' }
  ],
  filteredValue: filteredInfo.invoiceStatus || null,
  render: (_, record) => {
    const isEditing = editingId === record._id && editingField === 'invoiceStatus';
    const status = record.invoiceStatus || 'INVOICE-NOT-SENT';
    
    // Get latest invoice status change date and convert to PST
    const getLatestInvoiceDatePST = () => {
      if (record.invoiceStatusHistory && record.invoiceStatusHistory.length > 0) {
        const latest = [...record.invoiceStatusHistory].sort((a, b) => 
          new Date(b.changedAt) - new Date(a.changedAt)
        )[0];
        return toPST(latest.changedAt);
      }
      return null;
    };
    
    const latestInvoiceDatePST = getLatestInvoiceDatePST();
    
    const colors = {
      'INVOICE-SENT': 'success',
      'INVOICE-NOT-SENT': 'warning'
    };
    const labels = {
      'INVOICE-SENT': '✓ Sent',
      'INVOICE-NOT-SENT': '⚠ Not Sent'
    };

    if (isEditing) {
      return (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Select
            value={editingValue}
            onChange={(value) => setEditingValue(value)}
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="INVOICE-SENT">✓ Invoice Sent</Option>
            <Option value="INVOICE-NOT-SENT">⚠ Invoice Not Sent</Option>
          </Select>
          <Space size={4}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />} 
              onClick={() => handleEditSave(record._id)}
              style={{ flex: 1, borderRadius: 6 }}
            />
            <Button 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={handleEditCancel}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </Space>
      );
    }

    return (
      <div>
        <Space size={4}>
          <Tag 
            color={colors[status] || 'default'} 
            style={{ padding: '4px 12px', borderRadius: 16, fontWeight: 500, cursor: 'pointer' }}
            onClick={() => handleEditStart(record, 'invoiceStatus')}
          >
            {labels[status] || status}
          </Tag>
          <Tooltip title="Edit Invoice Status">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditStart(record, 'invoiceStatus')}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
        {latestInvoiceDatePST && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={latestInvoiceDatePST.format('MMM-DD-YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {latestInvoiceDatePST.format('MM/DD/YY · hh:mm A')} PST
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
{
  title: 'PO Status',
  key: 'purchaseOrderStatus',
  width: 200,
  align: 'center',
  filters: [
    { text: 'PO Sent', value: 'PO-SENT' },
    { text: 'PO Not Sent', value: 'PO-NOT-SENT' },
    { text: 'PO Confirmed', value: 'PO-CONFIRMED' },
    { text: 'PO Not Confirmed', value: 'PO-NOT-CONFIRMED' }
  ],
  filteredValue: filteredInfo.purchaseOrderStatus || null,
  render: (_, record) => {
    const isEditing = editingId === record._id && editingField === 'purchaseOrderStatus';
    const status = record.purchaseOrderStatus || 'PO-NOT-SENT';
    
    // Get latest PO status change date and convert to PST
    const getLatestPODatePST = () => {
      if (record.purchaseOrderStatusHistory && record.purchaseOrderStatusHistory.length > 0) {
        const latest = [...record.purchaseOrderStatusHistory].sort((a, b) => 
          new Date(b.changedAt) - new Date(a.changedAt)
        )[0];
        return toPST(latest.changedAt);
      }
      return null;
    };
    
    const latestPODatePST = getLatestPODatePST();
    
    const colors = {
      'PO-SENT': 'processing',
      'PO-NOT-SENT': 'warning',
      'PO-CONFIRMED': 'success',
      'PO-NOT-CONFIRMED': 'error'
    };
    const labels = {
      'PO-SENT': '📦 Sent',
      'PO-NOT-SENT': '⚠ Not Sent',
      'PO-CONFIRMED': '✓ Confirmed',
      'PO-NOT-CONFIRMED': '❌ Not Confirmed'
    };

    if (isEditing) {
      return (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Select
            value={editingValue}
            onChange={(value) => setEditingValue(value)}
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="PO-SENT">📦 PO Sent</Option>
            <Option value="PO-NOT-SENT">⚠ PO Not Sent</Option>
            <Option value="PO-CONFIRMED">✓ PO Confirmed</Option>
            <Option value="PO-NOT-CONFIRMED">❌ PO Not Confirmed</Option>
          </Select>
          <Space size={4}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />} 
              onClick={() => handleEditSave(record._id)}
              style={{ flex: 1, borderRadius: 6 }}
            />
            <Button 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={handleEditCancel}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </Space>
      );
    }

    return (
      <div>
        <Space size={4}>
          <Tag 
            color={colors[status] || 'default'} 
            style={{ padding: '4px 12px', borderRadius: 16, fontWeight: 500, cursor: 'pointer' }}
            onClick={() => handleEditStart(record, 'purchaseOrderStatus')}
          >
            {labels[status] || status}
          </Tag>
          <Tooltip title="Edit PO Status">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditStart(record, 'purchaseOrderStatus')}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
        {latestPODatePST && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={latestPODatePST.format('MMM-DD-YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {latestPODatePST.format('MM/DD/YY · hh:mm A')} PST
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
{
    title: 'Comment',
    key: 'trackingPageComment',
    width: 280,
    render: (_, record) => {
        const comments = record.trackingPageComment || [];
        const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
        
        return (
            <div>
             

                {/* Display Latest Comment */}
                {latestComment ? (
                    <div 
                        style={{
                            background: '#fafafa',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            wordBreak: 'break-word',      // Add this
                            wordWrap: 'break-word',       // Add this
                            overflowWrap: 'break-word',   // Add this
                            whiteSpace: 'normal'          // Add this
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatCommentDate(latestComment.changedAt)}
                            </Text>
                            {comments.length > 1 && (
                                <Tag 
                                    color="blue" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '0 4px', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLeadForComment(record);
                                        setCommentsModalVisible(true);
                                    }}
                                >
                                    +{comments.length - 1} more
                                </Tag>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            display: 'block', 
                            wordBreak: 'break-word',    // Add this
                            whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                        }}>
                            {latestComment.comment.length > 80 
                                ? `${latestComment.comment.substring(0, 80)}...` 
                                : latestComment.comment}
                        </Text>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            textAlign: 'center', 
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                            No comments yet
                        </Text>
                    </div>
                )}
                   <Button
                    type="primary"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLeadForComment(record);
                        setNewComment('');
                        setCommentsModalVisible(true);
                    }}
                    style={{ 
                        marginBottom: '12px',
                        width: '100%',
                        fontSize: '12px'
                    }}
                >
                    Add Comment
                </Button>
            </div>
        );
    },
},
   {
    title: 'Date Info',
    key: 'dateInfo',
    width: 200,
    sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    render: (_, record) => (
        <Tooltip
            title={
                <>
                    <div><strong>Created:</strong> {record.createdAt ? dayjs(record.createdAt).format('MMM-DD-YYYY hh:mm A') : 'N/A'}</div>
                    <div><strong>Updated:</strong> {record.updatedAt ? dayjs(record.updatedAt).format('MMM-DD-YYYY hh:mm A') : 'N/A'}</div>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                {/* Created */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#F1EFE8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                            {record.createdAt ? dayjs(record.createdAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Created · {record.createdAt ? dayjs(record.createdAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

                {/* Updated */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#E6F1FB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                            {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

            </div>
        </Tooltip>
    ),
},
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Lead Details">
          <Button 
            type="primary"
            shape="circle"
            icon={<EyeOutlined />} 
            onClick={() => handleView(record)}
            style={{ 
              background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
              border: 'none',
              boxShadow: '0 2px 8px rgba(24,144,255,0.3)',
              width: 32,
              height: 32
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const visibleColumns = columns
    .filter((column) => !['zohoStatus', 'invoiceStatus', 'purchaseOrderStatus'].includes(column.key))
    .map((column) => {
      if (column.key !== 'dateInfo') {
        return column;
      }

      return {
        ...column,
        sorter: (a, b) => new Date(getPOStatusConfirmedDate(a) || 0) - new Date(getPOStatusConfirmedDate(b) || 0),
        render: (_, record) => {
          const confirmedDate = getPOStatusConfirmedDate(record);
          const confirmedDatePST = toPST(confirmedDate);
          const confirmedByName = confirmedDatePST ? (getPOStatusConfirmedByName(record) || 'Unknown') : null;

          return (
            <Tooltip
              title={
                confirmedDatePST
                  ? `PO Status Confirmed Date: ${confirmedDatePST.format('MMM-DD-YYYY hh:mm A')} PST`
                  : 'PO Status Confirmed Date: N/A'
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: '#EAF7EE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <CheckCircleOutlined style={{ fontSize: 13, color: '#237804' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#237804' }}>
                      {confirmedDatePST ? confirmedDatePST.format('MM/DD/YY hh:mm A') : 'N/A'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                      {confirmedDatePST ? `PO Status Confirmed Date | ${confirmedDatePST.fromNow()}` : 'PO Status Confirmed Date'}
                    </Text>
                    {confirmedByName && (
                      <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                        Confirmed by: {confirmedByName}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            </Tooltip>
          );
        },
      };
    });

  return (
    <div style={{ 
      padding: 24, 
      minHeight: "100vh", 
      background: `linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)`
    }}>
      {/* Modern Header with Stats */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card 
          style={{ 
            marginBottom: 24, 
            borderRadius: 24,
            border: 'none'
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} md={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Avatar 
                  size={72} 
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    color:"black"
                  }}
                  icon={<TruckOutlined style={{ fontSize: 36 }} />}
                />
                <div>
                 <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
  Pending Shipment Tracking
</Title>
<Text style={{ fontSize: 14 }}>
  Track and manage pending shipments awaiting delivery or parts availability
</Text>
                </div>
              </div>
            </Col>
         
          </Row>
        </Card>
      </motion.div>

      {/* Filter Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card 
          bordered={false}
          style={{ 
            borderRadius: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: 24,
            border: '1px solid #f0f0f0'
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Space.Compact block>
                <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
                <Input
                  placeholder={getSearchFieldsPlaceholder(searchFields)}
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                  size="large"
                />
              </Space.Compact>
            </Col>
            {isAdmin && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by Agent"
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                  style={{ width: '100%', borderRadius: 12 }}
                  size="large"
                  allowClear
                  suffixIcon={<TeamOutlined />}
                  showSearch
                  optionFilterProp="children"
                >
                  {agents.map(agent => (
                    <Option key={agent._id} value={agent._id}>
                      <Space>
                        <Avatar size="small" icon={<UserOutlined />} />
                        <span>{agent.firstName} {agent.lastName}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} md={4}>
              <Select
                placeholder="Quick Date"
                style={{ width: '100%', borderRadius: 12 }}
                onChange={handleQuickDateFilter}
                value={quickDateFilter}
                size="large"
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="thisWeek">This Week</Option>
                <Option value="thisMonth">This Month</Option>
                <Option value="lastMonth">Last Month</Option>
                <Option value="thisQuarter">This Quarter</Option>
                <Option value="thisYear">This Year</Option>
                <Option value="custom">Custom Range</Option>
              </Select>
            </Col>
            <Col xs={24} md={isAdmin ? 7 : 8}>
    <RangePicker
      style={{ width: '100%', borderRadius: 12 }}
      onChange={handleDateRangeChange}
      value={dateRange}
      size="large"
      format="MM/DD/YYYY"
      placeholder={['Start Date', 'End Date']}
      allowClear={false} // Add this to prevent clearing
    />
  </Col>

            <Col xs={24} md={isAdmin ? 4 : 6}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Tooltip title="Refresh">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    loading={refreshing}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
                <Tooltip title="Clear Filters">
                  <Button 
                    icon={<ClearOutlined />} 
                    onClick={handleClearFilters}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>

      {dateRange && dateRange[0] && dateRange[1] && (
    <div style={{ 
      marginTop: 16,
      backgroundColor: '#f0f5ff', 
      padding: '8px 12px', 
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }}>
      <CalendarOutlined style={{ color: '#1890ff' }} />
      <Text type="secondary">
        Showing orders from: <Text strong>{dateRange[0].format('MM/DD/YYYY')}</Text> to <Text strong>{dateRange[1].format('MM/DD/YYYY')}</Text>
      </Text>
      <Badge 
        count={totalRecords} 
        showZero 
        color="#52c41a" 
        style={{ marginLeft: 'auto' }}
      />
    </div>
  )}
        </Card>
      </motion.div>

    <div style={{
    padding: '16px 20px',
    background: '#ffffff',
    borderTop: '1px solid #f0f0f0',
    borderBottom: '1px solid #f0f0f0',
    borderRadius: '12px 12px 0 0',
    marginTop: 8
  }}>
    <Row justify="space-between" align="middle">
      <Col>
        <Space size={16}>
          <Text strong style={{ fontSize: 16 }}>
            {isSearching ? (
              <>
                <SearchOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                Search Results
              </>
            ) : (
              <>
                <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {dateRange && dateRange[0] && dateRange[1] ? (
                  dateRange[0].isSame(dateRange[1], 'day') ? (
                    dateRange[0].format('MM/DD/YYYY')
                  ) : (
                    `${dateRange[0].format('MMM D')} → ${dateRange[1].format('MM/DD/YYYY')}`
                  )
                ) : "Selected Period"}
              </>
            )}
          </Text>
          <Badge
            count={leads.length}
            showZero
            style={{
              backgroundColor: '#52c41a',
              fontSize: 12,
              fontWeight: 'bold'
            }}
          />
          <Text type="secondary">
            of {totalRecords.toLocaleString()} total records
          </Text>
        </Space>
      </Col>
      <Col>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalRecords}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onChange={handlePaginationChange}
          size="small"
          simple
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
        />
      </Col>
    </Row>
  </div>

      {/* Main Table */}
      <Card 
        bordered={false}
        style={{ 
          borderRadius: '0 0 20px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          borderTop: 'none'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={visibleColumns}
          dataSource={leads}
          rowKey="_id"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
          scroll={{ x: isAdmin ? 1600 : 1400, y: 'calc(100vh - 550px)' }}
          bordered={false}
          size="middle"
          rowClassName={() => 'table-row-hover'}
          style={{
            background: 'transparent'
          }}
        />

        {/* Bottom Pagination */}
        {leads.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 32,
            padding: '20px 24px',
            background: '#fafafa',
            borderRadius: 12,
            margin: 24
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={handlePaginationChange}
              showQuickJumper
              showTotal={(total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} shipment records`
              }
              size="default"
            />
          </div>
        )}
      </Card>
{/* Edit Shipment Modal */}
<Modal
  title={
    <Space>
      <EditOutlined style={{ color: '#1890ff' }} />
      <Text strong style={{ fontSize: 18 }}>Edit Shipment Details</Text>
    </Space>
  }
  open={modalVisible}
  onCancel={() => {
    setModalVisible(false);
    form.resetFields();
    setSelectedRecord(null);
    setEditingShipmentDate(null);
  }}
  footer={[
    <Button 
      key="cancel" 
      onClick={() => {
        setModalVisible(false);
        form.resetFields();
        setSelectedRecord(null);
        setEditingShipmentDate(null);
      }} 
      icon={<CloseOutlined />} 
      disabled={updating}
    >
      Cancel
    </Button>,
<Button 
  key="save" 
  type="primary" 
  onClick={async () => {
    if (!editingShipmentDate) {
      message.warning('Please select a date');
      return;
    }
    
    try {
      setUpdating(true);
      const values = await form.validateFields();

      const getPriorityFromPartStatus = (status) => {
        if (status === "Available" || status === "Customer Canceled Order") {
          return "Medium";
        }
        if (status === "Not Available" || status === "Not Good" || status === "Wrong Part") {
          return "High";
        }
        return "Low";
      };
      
      let purchaseOrderStatus;
      
      if (
        values.partStatus === "Not Available" ||
        values.partStatus === "Not Good" ||
        values.partStatus === "Wrong Part"
      ) {
        purchaseOrderStatus = "PO-NOT-SENT";
      }
      
      const payload = {
        ShipmentTracking: {
          ...selectedRecord?.ShipmentTracking,
          trackingStatus: values.trackingStatus,
          shippingCompanyName: values.shippingCompanyName,
          partStatus: values.partStatus,
          picturesApproval: values.picturesApproval,
          picturesStatus: values.picturesStatus,
          priority: values.priority,
        },
        ShipmentTrackingUpdatedAt: editingShipmentDate.toISOString() // ✅ Send the selected date
      };

      if (purchaseOrderStatus) {
        payload.purchaseOrderStatus = purchaseOrderStatus;
      }

      const orderProcessCompleteUpdate = {};

      if (values.partStatus === "Customer Canceled Order") {
        orderProcessCompleteUpdate.partStatus = "Customer Canceled";
      } else {
        orderProcessCompleteUpdate.partStatus = "Customer Accepted";
      }
      
      if (Object.keys(orderProcessCompleteUpdate).length > 0) {
        payload.orderProcessComplete = {
          ...selectedRecord?.orderProcessComplete,
          ...orderProcessCompleteUpdate
        };
      }
      
      const response = await API.UpdateLead(selectedRecord._id, payload);
      
      if (response?.status || response?.success) {
        message.success("Shipment tracking updated successfully!");
        setModalVisible(false);
        form.resetFields();
        setSelectedRecord(null);
        setEditingShipmentDate(null);
        fetchLeads();
      } else {
        message.error(response?.message || "Failed to update shipment tracking");
      }
    } catch (error) {
      console.error("Error updating shipment tracking:", error);
      message.error("Failed to update shipment tracking");
    } finally {
      setUpdating(false);
    }
  }} 
  icon={<SaveOutlined />}
  loading={updating}
>
  Save Changes
</Button>
  ]}
  width={800}
  style={{ top: 30 }}
  bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}
>
  <Form
    form={form}
    layout="vertical"
  >
   
    
    <Divider style={{ margin: '12px 0 20px 0' }} />
    
    <Row gutter={24}>
      <Col span={12}>
        <Form.Item
          name="trackingStatus"
          label="Tracking Number"
          rules={[{ required: true, message: 'Please enter tracking number' }]}
        >
          <Input 
            prefix={<ClockCircleOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Enter tracking number"
            style={{ borderRadius: 8 }}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          name="shippingCompanyName"
          label="Shipping Company Name"
          rules={[{ required: true, message: 'Please enter shipping company name' }]}
        >
          <Input
            prefix={<TruckOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Enter shipping company name (e.g., UPS, FedEx, DHL, USPS)"
            style={{ borderRadius: 8 }}
          />
        </Form.Item>
      </Col>
    </Row>

    {/* Delivery Status now lives on the Delivery Tracking page, once these
        required fields are complete the lead moves there automatically. */}
    <Row gutter={24}>
      <Col span={24}>
        <Form.Item
          name="partStatus"
          label="Part Status"
          rules={[{ required: true, message: 'Please select part status' }]}
        >
          <Select
            placeholder="Select part status"
            style={{ borderRadius: 8 }}
            onChange={(value) => {
              const priority = getPriorityFromPartStatus(value);
              form.setFieldsValue({ priority });
              
              if (value === "Customer Canceled Order") {
                message.warning("This will update Order Process Complete to 'Customer Canceled'");
              }
            }}
          >
            <Option value="Available">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span style={{ color: '#52c41a' }}>Available</span>
              </Space>
            </Option>
            <Option value="Not Available">
              <Space>
                <CloseCircleOutlined style={{ color: '#f5222d' }} />
                <span style={{ color: '#f5222d' }}>Not Available</span>
              </Space>
            </Option>
            <Option value="Not Good">
              <Space>
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                <span style={{ color: '#faad14' }}>Not Good</span>
              </Space>
            </Option>
            <Option value="Wrong Part">
              <Space>
                <CloseCircleOutlined style={{ color: '#f5222d' }} />
                <span style={{ color: '#f5222d' }}>Wrong Part</span>
              </Space>
            </Option>
            <Option value="Customer Canceled Order">
              <Space>
                <StopOutlined style={{ color: '#8c8c8c' }} />
                <span style={{ color: '#8c8c8c' }}>Customer Canceled Order</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={24}>
      <Col span={12}>
        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: 'Please select priority' }]}
        >
          <Select 
            placeholder="Select priority"
            style={{ borderRadius: 8 }}
          >
            <Option value="High">
              <Space>
                <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
                <span>High</span>
              </Space>
            </Option>
            <Option value="Medium">
              <Space>
                <FlagOutlined style={{ color: '#fa8c16' }} />
                <span>Medium</span>
              </Space>
            </Option>
            <Option value="Low">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Low</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          name="picturesApproval"
          label="Pictures Approval"
          rules={[{ required: true, message: 'Please select pictures approval' }]}
        >
          <Select 
            placeholder="Select approval status"
            style={{ borderRadius: 8 }}
          >
            <Option value="Yes">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Yes</span>
              </Space>
            </Option>
            <Option value="No">
              <Space>
                <CloseCircleOutlined style={{ color: '#f5222d' }} />
                <span>No</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={24}>
      <Col span={12}>
        <Form.Item
          name="picturesStatus"
          label="Pictures Status"
          rules={[{ required: true, message: 'Please select pictures status' }]}
        >
          <Select 
            placeholder="Select pictures status"
            style={{ borderRadius: 8 }}
          >
            <Option value="Received">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Received</span>
              </Space>
            </Option>
            <Option value="Not Received">
              <Space>
                <SyncOutlined spin style={{ color: '#fa8c16' }} />
                <span>Not Received</span>
              </Space>
            </Option>
            <Option value="Pending">
              <Space>
                <ClockCircleOutlined style={{ color: '#faad14' }} />
                <span>Pending</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>

     {/* Date Picker Section - Add this at the top of the form */}
    <div style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong>SELECT Date</Text>
      </div>
      <DatePicker
        value={editingShipmentDate}
        onChange={setEditingShipmentDate}
        format="MM/DD/YYYY"
        style={{ width: '100%' }}
        size="large"
        placeholder="Select date"
      />
     
    </div>
  </Form>
</Modal>
      <style>{`
        .table-row-hover:hover {
          background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%) !important;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0 !important;
        }
        
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          color: #1f2937 !important;
          border-bottom: 2px solid #f0f0f0 !important;
        }
      `}</style>

               <Modal
         
          open={commentsModalVisible}
          onCancel={() => {
              setCommentsModalVisible(false);
              setSelectedLeadForComment(null);
              setNewComment('');
          }}
          footer={[
              <Button key="cancel" onClick={() => {
                  setCommentsModalVisible(false);
                  setSelectedLeadForComment(null);
                  setNewComment('');
              }}>
                  Cancel
              </Button>,
              <Button 
                  key="submit" 
                  type="primary" 
                  onClick={handleAddComment}
                  loading={addingComment}
                  disabled={!newComment.trim()}
              >
                  Add Comment
              </Button>
          ]}
          width={500}
      >
          <div style={{ padding: '16px 0' }}>
            
      
              <div>
                  <Text strong>Comment:</Text>
                  <Input.TextArea
                      placeholder="Enter your comment here..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                      maxLength={1000}
                      showCount
                      style={{ marginTop: '8px' }}
                      autoFocus
                  />
              </div>
      
              {/* Show existing comments if any */}
              {selectedLeadForComment?.trackingPageComment?.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                      <Divider />
                      <Text strong>Previous Comments ({selectedLeadForComment.trackingPageComment.length})</Text>
                      <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          marginTop: '8px',
                          padding: '8px',
                          background: '#f5f5f5',
                          borderRadius: '8px'
                      }}>
                          {[...selectedLeadForComment.trackingPageComment]
                              .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                              .map((comment, idx) => (
                                  <div key={idx} style={{ 
                                      marginBottom: '12px', 
                                      padding: '8px',
                                      background: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #e8e8e8'
                                  }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                          <Text type="secondary" style={{ fontSize: '11px' }}>
                                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                              {formatCommentDate(comment.changedAt)}
                                          </Text>
                                      </div>
                                      <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                                  </div>
                              ))}
                      </div>
                  </div>
              )}
          </div>
      </Modal>
      
{/* Service Agent Assignment Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <TeamOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>Assign Service Agent</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Select service agent for this lead
                </Text>
            </div>
        </div>
    }
    open={serviceAgentModalVisible}
    onCancel={() => {
        setServiceAgentModalVisible(false);
        setSelectedLeadForServiceAgent(null);
        setSelectedServiceAgentForLead(null);
    }}
    footer={null}
    width={500}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {selectedLeadForServiceAgent && (
            <>
                {/* Customer Info Card */}
                <Card 
                    size="small" 
                    style={{ 
                        marginBottom: 20,
                        background: '#f5f5f5',
                        borderRadius: 12,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>
                            {selectedLeadForServiceAgent.personalInfo?.fullName || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedLeadForServiceAgent.vehicleInfo?.make} {selectedLeadForServiceAgent.vehicleInfo?.model} - {selectedLeadForServiceAgent.vehicleInfo?.part}
                        </Text>
                        {selectedLeadForServiceAgent.serviceHandledBy && (
                            <div style={{ 
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid #e8e8e8'
                            }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Currently assigned: 
                                </Text>
                                <Text strong style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                    {getServiceAgentName(selectedLeadForServiceAgent)}
                                </Text>
                            </div>
                        )}
                    </Space>
                </Card>

                {/* Service Agent Selection */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Service Agent:
                    </Text>
                    <Select
                        value={selectedServiceAgentForLead}
                        onChange={setSelectedServiceAgentForLead}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="Choose an agent"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        loading={loadingServiceAgents}
                    >
                        {Object.entries(serviceAgentsMap).map(([agentId, agentInfo]) => (
                            <Option key={agentId} value={agentId}>
                                <Space direction="vertical" size={0}>
                                    <Text strong>{agentInfo.name}</Text>
                             
                                </Space>
                            </Option>
                        ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {selectedServiceAgentForLead ? 'Agent will be assigned to this lead' : 'Clear to unassign the current agent'}
                    </Text>
                </div>

                {/* Modal Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={() => {
                            setServiceAgentModalVisible(false);
                            setSelectedLeadForServiceAgent(null);
                            setSelectedServiceAgentForLead(null);
                        }} 
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleServiceAgentSave}
                        loading={savingServiceAgent}
                        size="large"
                        icon={<CheckOutlined />}
                    >
                        {selectedServiceAgentForLead ? 'Assign Agent' : 'Unassign Agent'}
                    </Button>
                </div>
            </>
        )}
    </div>
</Modal>
    </div>
  );
};

export default ShipmentTracking;

